import { useState, useRef, useEffect } from 'react'
import { Bell, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useNotifications } from '@/hooks/useNotifications'
import NotificationItem from '@/components/list/NotificationItem'
import { cn } from '@/lib/utils'

export function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    refresh,
  } = useNotifications()

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Refresh notifications when opening
  const handleToggle = () => {
    if (!isOpen) {
      refresh()
    }
    setIsOpen(!isOpen)
  }

  const handleMarkAllAsRead = async () => {
    await markAllAsRead()
  }

  const handleMarkAsRead = async (notificationId: string) => {
    await markAsRead(notificationId)
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'relative text-[#A0A0B0] hover:text-[#FF2D7B] hover:bg-[#FF2D7B]/10',
          isOpen && 'text-[#FF2D7B] bg-[#FF2D7B]/10'
        )}
        onClick={handleToggle}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#FF2D7B] text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 rounded-xl border border-[#1E1E28] bg-[#12121A] shadow-2xl shadow-black/50 z-50">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[#1E1E28] p-4">
            <h3 className="font-display text-lg font-semibold text-[#FAFAFA]">
              Notifications
            </h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllAsRead}
                className="gap-1 text-xs text-[#A0A0B0] hover:text-[#FF2D7B] hover:bg-[#FF2D7B]/10"
              >
                <Check className="h-3 w-3" />
                Mark all read
              </Button>
            )}
          </div>

          {/* Notifications list */}
          <div className="max-h-[500px] overflow-y-auto">
            {isLoading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#FF2D7B] border-t-transparent"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <Bell className="h-12 w-12 text-[#64647A] mb-3" />
                <p className="text-sm text-[#A0A0B0]">No notifications yet</p>
                <p className="text-xs text-[#64647A] mt-1">
                  When someone interacts with your content, you'll see it here
                </p>
              </div>
            ) : (
              <div className="divide-y divide-[#1E1E28]">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t border-[#1E1E28] p-3 text-center">
              <button
                onClick={() => setIsOpen(false)}
                className="text-xs text-[#A0A0B0] hover:text-[#FF2D7B] transition-colors"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
